import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Inject,
	Input,
	NgZone,
	OnChanges,
	OnDestroy,
	OnInit,
	Output,
	PLATFORM_ID,
	SimpleChanges,
	ViewChild,
	ViewEncapsulation
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { FormControl } from '@angular/forms';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSliderChange } from '@angular/material/slider';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { LocationSelection } from './location-picker.types';

@Component({
	selector: 'app-location-picker',
	templateUrl: './location-picker.component.html',
	styleUrls: ['./location-picker.component.scss'],
	encapsulation: ViewEncapsulation.None
})
export class LocationPickerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
	@Input() locationCity?: string;
	@Input() locationState?: string;
	@Input() latitude?: number;
	@Input() longitude?: number;
	@Input() locationRadius?: string;
	@Input() locationLabel?: string;
	@Input() appearance: 'outline' | 'fill' = 'outline';
	@Input() placeholderKey = 'locationPicker.placeholder';

	@Input() isOutLabel?: boolean;

	@Input() variant?: boolean;

	@Output() readonly selectionChange = new EventEmitter<LocationSelection>();
	@Output() readonly cleared = new EventEmitter<void>();

	@ViewChild('attributionEl') private readonly attributionEl!: ElementRef<HTMLDivElement>;
	@ViewChild(MatAutocompleteTrigger) private readonly autocompleteTrigger?: MatAutocompleteTrigger;

	readonly searchControl = new FormControl<string>('');
	readonly radiusControl = new FormControl<number>(2);

	predictions: google.maps.places.AutocompletePrediction[] = [];
	isLoading = false;
	showNoResults = false;
	showTooGeneral = false;
	locationType: 'city' | 'coords' | null = null;
	hasSelection = false;
	displayRadius = 2;

	get showRadiusSlider(): boolean {
		return this.hasSelection && this.latitude != null && this.longitude != null;
	}

	readonly displayFn = (value: google.maps.places.AutocompletePrediction | string | null): string => {
		if (!value) return '';
		return typeof value === 'string' ? value : value.description;
	};

	private autocompleteService: google.maps.places.AutocompleteService | null = null;
	private placesService: google.maps.places.PlacesService | null = null;
	private readonly destroy$ = new Subject<void>();
	private readonly minQueryLength = 3;
	private currentDisplayText = '';

	private readonly TOO_GENERAL_TYPES = ['country', 'administrative_area_level_1', 'administrative_area_level_2'];
	private readonly CITY_TYPES = ['locality', 'sublocality_level_1', 'administrative_area_level_3'];

	constructor(
		@Inject(PLATFORM_ID) private readonly platformId: Object,
		private readonly ngZone: NgZone,
		private readonly cdr: ChangeDetectorRef
	) {}

	private normalizeRadius(value?: string | number): number {
		if (value == null) {
			return 2;
		}
		const num = typeof value === 'string' ? Number.parseFloat(value) : value;
		return Number.isFinite(num) && num >= 1 ? num : 2;
	}

	ngOnInit(): void {
		this.initFromInputs();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (
			'locationCity' in changes ||
			'locationState' in changes ||
			'latitude' in changes ||
			'longitude' in changes ||
			'locationRadius' in changes ||
			'locationLabel' in changes
		) {
			this.initFromInputs();
		}
	}

	ngAfterViewInit(): void {
		if (isPlatformBrowser(this.platformId)) {
			this.initPlacesServices();
			this.setupValueChanges();
		}
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private initFromInputs(): void {
		this.locationType = null;
		this.hasSelection = false;
		this.displayRadius = this.normalizeRadius(this.locationRadius);
		this.radiusControl.setValue(this.displayRadius, { emitEvent: false });

		if (this.locationCity) {
			const display = this.locationState
				? `${this.locationCity}, ${this.locationState}`
				: this.locationLabel ?? this.locationCity;
			this.searchControl.setValue(display, { emitEvent: false });
			this.currentDisplayText = display;
			this.locationType = 'city';
			this.hasSelection = true;
			return;
		}

		if (this.latitude != null && this.longitude != null) {
			this.locationType = 'coords';
			this.hasSelection = true;
			if (this.locationLabel) {
				this.searchControl.setValue(this.locationLabel, { emitEvent: false });
				this.currentDisplayText = this.locationLabel;
			} else {
				this.searchControl.setValue('', { emitEvent: false });
			}
			return;
		}

		this.searchControl.setValue('', { emitEvent: false });
		this.currentDisplayText = '';
	}

	private initPlacesServices(): void {
		if (typeof google === 'undefined' || !google.maps?.places) {
			return;
		}
		this.autocompleteService = new google.maps.places.AutocompleteService();
		if (this.attributionEl?.nativeElement) {
			this.placesService = new google.maps.places.PlacesService(this.attributionEl.nativeElement);
		}
	}

	private setupValueChanges(): void {
		this.searchControl.valueChanges
			.pipe(
				takeUntil(this.destroy$),
				map(value => (typeof value === 'string' ? value : '')),
				debounceTime(300),
				distinctUntilChanged()
			)
			.subscribe(value => this.fetchPredictions(value));
	}

	onRadiusInput(event: MatSliderChange): void {
		this.displayRadius = event.value ?? 2;
	}

	onRadiusChange(event: MatSliderChange): void {
		const radius = event.value ?? 2;
		this.displayRadius = radius;
		this.radiusControl.setValue(radius, { emitEvent: false });
		if (this.locationType === 'coords' && this.hasSelection) {
			this.emitCoords(this.latitude!, this.longitude!, this.currentDisplayText, String(Math.max(1, radius)));
		}
	}

	private fetchPredictions(query: string): void {
		const normalized = query.trim();
		if (normalized.length < this.minQueryLength || !this.autocompleteService) {
			this.predictions = [];
			this.showNoResults = false;
			this.isLoading = false;
			this.autocompleteTrigger?.closePanel();
			return;
		}

		this.isLoading = true;
		this.showNoResults = false;
		this.showTooGeneral = false;
		this.openAutocompletePanel();

		this.autocompleteService.getPlacePredictions({ input: normalized }, (results, status) => {
			this.ngZone.run(() => {
				this.isLoading = false;
				if (status === google.maps.places.PlacesServiceStatus.OK && results?.length) {
					this.predictions = results;
					this.showNoResults = false;
				} else {
					this.predictions = [];
					this.showNoResults = true;
				}
				this.openAutocompletePanel();
			});
		});
	}

	onPlaceSelected(prediction: google.maps.places.AutocompletePrediction): void {
		if (!this.placesService) {
			return;
		}

		this.isLoading = true;
		this.showTooGeneral = false;

		this.placesService.getDetails(
			{ placeId: prediction.place_id, fields: ['geometry', 'address_components', 'types'] },
			(place, status) => {
				this.ngZone.run(() => {
					this.isLoading = false;

					if (status !== google.maps.places.PlacesServiceStatus.OK || !place) {
						return;
					}

					const types = place.types ?? [];

					if (this.isTooGeneral(types)) {
						this.showTooGeneral = true;
						this.searchControl.setErrors({ tooGeneral: true });
						this.searchControl.setValue(this.currentDisplayText, { emitEvent: false });
						return;
					}

					const components = place.address_components ?? [];

					if (this.isCity(types)) {
						const city = this.extractComponent(components, 'locality') ?? '';
						const state = this.extractComponent(components, 'administrative_area_level_1', 'short_name') ?? '';
						const displayText = city && state ? `${city}, ${state}` : city || state;

						this.currentDisplayText = displayText;
						this.searchControl.setErrors(null);
						this.searchControl.setValue(displayText, { emitEvent: false });
						this.locationType = 'city';
						this.hasSelection = true;
						this.latitude = undefined;
						this.longitude = undefined;
						this.predictions = [];
						this.autocompleteTrigger?.closePanel();

						this.selectionChange.emit({ type: 'city', displayText, locationCity: city, locationState: state });
						return;
					}

					if (!place.geometry?.location) {
						return;
					}

					const lat = place.geometry.location.lat();
					const lng = place.geometry.location.lng();
					const displayText = prediction.description;
					const radius = String(Math.max(1, this.radiusControl.value ?? 2));

					this.currentDisplayText = displayText;
					this.searchControl.setErrors(null);
					this.searchControl.setValue(displayText, { emitEvent: false });
					this.locationType = 'coords';
					this.hasSelection = true;
					this.latitude = lat;
					this.longitude = lng;
					this.predictions = [];
					this.autocompleteTrigger?.closePanel();

					this.emitCoords(lat, lng, displayText, radius);
				});
			}
		);
	}

	onClear(): void {
		this.searchControl.setValue('', { emitEvent: false });
		this.locationType = null;
		this.hasSelection = false;
		this.predictions = [];
		this.showNoResults = false;
		this.showTooGeneral = false;
		this.autocompleteTrigger?.closePanel();
		this.currentDisplayText = '';
		this.latitude = undefined;
		this.longitude = undefined;
		this.locationRadius = undefined;
		this.radiusControl.setValue(2, { emitEvent: false });
		this.displayRadius = 2;
		this.cleared.emit();
	}

	private emitCoords(lat: number, lng: number, displayText: string, radius: string): void {
		this.selectionChange.emit({ type: 'coords', displayText, latitude: lat, longitude: lng, locationRadius: radius });
	}

	private openAutocompletePanel(): void {
		this.cdr.detectChanges();
		this.autocompleteTrigger?.openPanel();
	}

	private isTooGeneral(types: string[]): boolean {
		return types.some(t => this.TOO_GENERAL_TYPES.includes(t));
	}

	private isCity(types: string[]): boolean {
		return types.some(t => this.CITY_TYPES.includes(t));
	}

	private extractComponent(
		components: google.maps.GeocoderAddressComponent[],
		type: string,
		nameType: 'long_name' | 'short_name' = 'long_name'
	): string | undefined {
		return components.find(c => c.types.includes(type))?.[nameType];
	}
}
